import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, type AdminRecruiter } from '@/services/adminApi';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Platform default commission fraction (mirrors backend COMMISSION_RATE = 0.15). */
const DEFAULT_COMMISSION = 0.15;

function formatUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function toPercent(rate: number | null): number {
  return Math.round((rate ?? DEFAULT_COMMISSION) * 100);
}

export function AdminRecruitersPage(): JSX.Element {
  const { t } = useTranslation();
  const [recruiters, setRecruiters] = useState<AdminRecruiter[]>([]);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);

  // Per-row editing state (percent string), saving flag, and error.
  const [pct, setPct] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    setStatus('loading');
    setLoadError(null);
    try {
      const { recruiters } = await adminApi.listRecruiters();
      setRecruiters(recruiters);
      setPct(Object.fromEntries(recruiters.map((r) => [r.id, String(toPercent(r.recruiterCommissionRate))])));
      setStatus('ready');
    } catch (err) {
      setStatus('error');
      // Keep `t` out of the deps (loader re-runs on `t` identity churn from
      // i18n readiness would double-consume a reused mock Response). The render
      // falls back to `t('admin.recruiters.error')` when loadError is null.
      setLoadError(err instanceof Error ? err.message : null);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function handleSave(r: AdminRecruiter): Promise<void> {
    setSavingId(r.id);
    setRowError((prev) => ({ ...prev, [r.id]: '' }));
    try {
      const rate = Math.min(1, Math.max(0, (Number(pct[r.id]) || 0) / 100));
      const { recruiter } = await adminApi.setRecruiterCommission(r.id, rate);
      setRecruiters((prev) => prev.map((x) => (x.id === r.id ? { ...x, recruiterCommissionRate: recruiter.recruiterCommissionRate } : x)));
    } catch (err) {
      setRowError((prev) => ({ ...prev, [r.id]: err instanceof Error ? err.message : t('admin.recruiters.saveError') }));
    } finally {
      setSavingId(null);
    }
  }

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('admin.recruiters.title')}</h1>
        <p className="muted">{t('admin.recruiters.subtitle')}</p>
      </header>

      <section className="admin-card">
        {status === 'loading' && <p className="muted" role="status">{t('admin.recruiters.loading')}</p>}
        {status === 'error' && <p className="error" role="alert">{loadError ?? t('admin.recruiters.error')}</p>}
        {status === 'ready' && recruiters.length === 0 && (
          <p className="muted">{t('admin.recruiters.empty')}</p>
        )}
        {status === 'ready' && recruiters.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.recruiters.colRecruiter')}</th>
                  <th scope="col" className="num">{t('admin.recruiters.colClients')}</th>
                  <th scope="col" className="num">{t('admin.recruiters.colEarned')}</th>
                  <th scope="col" className="num">{t('admin.recruiters.colBalance')}</th>
                  <th scope="col" className="num">{t('admin.recruiters.colPlans')}</th>
                  <th scope="col">{t('admin.recruiters.colCommission')}</th>
                  <th scope="col" aria-label={t('admin.recruiters.colActions')} />
                </tr>
              </thead>
              <tbody>
                {recruiters.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div>{r.displayName ?? t('admin.recruiters.noName')}</div>
                      <div className="text-dim"><code>{r.email}</code></div>
                    </td>
                    <td className="num">{r.recruitedCount}</td>
                    <td className="num">{formatUsd(r.earnedCents)}</td>
                    <td className="num">{formatUsd(r.balanceCents)}</td>
                    <td className="num">{r.planCount}</td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={pct[r.id] ?? ''}
                        onChange={(e) => setPct((prev) => ({ ...prev, [r.id]: e.target.value }))}
                        aria-label={`${t('admin.recruiters.colCommission')} for ${r.email}`}
                        style={{ width: 72 }}
                      />
                      {r.recruiterCommissionRate === null && (
                        <span className="code-badge">{t('admin.recruiters.defaultBadge')}</span>
                      )}
                      {rowError[r.id] && <p className="error" role="alert">{rowError[r.id]}</p>}
                    </td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => void handleSave(r)}
                        disabled={savingId === r.id}
                        aria-label={`Save commission for ${r.email}`}
                      >
                        {savingId === r.id ? t('admin.recruiters.saving') : t('admin.recruiters.save')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted" style={{ marginTop: 8 }}>{t('admin.recruiters.commissionHint')}</p>
          </div>
        )}
      </section>
    </main>
  );
}
